import { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CreditCard, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui';
import { apiService } from '@/api';
import { useUserStore } from '@/store/userStore';
import { trackPurchase, trackCreditsPurchase } from '@/services/analyticsService';

const generateTransactionId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

// Pulls the numeric price out of a display string like "$14.99" / "USD 14.99"
// so we can echo it to GA/Meta as the purchase value. Falls back to 0 so we
// still fire a Purchase event even if the label is unparseable.
const parsePriceString = (s: string): number => {
  const match = s.match(/(\d+(?:[.,]\d+)?)/);
  if (!match) return 0;
  return parseFloat(match[1].replace(',', '.')) || 0;
};

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

// Same CDN artwork as the UpgradePage payment-methods strip.
const CARD_BRAND_LOGOS: Record<string, string> = {
  visa: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/visa.svg',
  mastercard: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/mastercard.svg',
  amex: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/amex.svg',
  discover: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/discover.svg',
  diners: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/diners.svg',
  jcb: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/jcb.svg',
  unionpay: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/unionpay.svg',
};

/** Persists across sessions so "Use another card" stays available after a failed charge. */
const PAYMENT_FAIL_FLAG_KEY = 'vivideo_direct_payment_failed';

export function hasDirectPaymentFailureFlag(): boolean {
  try {
    return localStorage.getItem(PAYMENT_FAIL_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDirectPaymentFailureFlag(): void {
  try {
    localStorage.setItem(PAYMENT_FAIL_FLAG_KEY, '1');
  } catch { /* private mode / quota */ }
}

export function clearDirectPaymentFailureFlag(): void {
  try {
    localStorage.removeItem(PAYMENT_FAIL_FLAG_KEY);
  } catch { /* ignore */ }
}

function CardBrandLogo({ brand }: { brand?: string }) {
  const key = (brand || '').toLowerCase();
  const src = CARD_BRAND_LOGOS[key];
  if (!src) {
    return <CreditCard className="w-8 h-8 text-text-secondary flex-shrink-0" />;
  }
  return (
    <img
      src={src}
      alt={CARD_BRAND_LABELS[key] || brand || 'Card'}
      className="h-8 w-auto max-w-[52px] object-contain flex-shrink-0 rounded-sm"
    />
  );
}

interface CardInfo {
  hasCard: boolean;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
}

interface PaymentAction {
  type: 'upgrade' | 'credits' | 'checkout';
  label: string;
  description: string;
  amount: string;
  priceId?: string;
  creditAmount?: number;
  onFallback?: () => void;
}

interface ConfirmPaymentModalProps {
  action: PaymentAction;
  onClose: () => void;
  onSuccess: () => void;
}

function ConfirmPaymentModal({ action, onClose, onSuccess }: ConfirmPaymentModalProps) {
  const { t } = useTranslation();
  const { refreshUser } = useUserStore();
  const { user } = useUserStore();
  const [cardInfo, setCardInfo] = useState<CardInfo | null>(null);
  const [proratedAmount, setProratedAmount] = useState<number | null>(null);
  const [isTrialUpgrade, setIsTrialUpgrade] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showUseAnotherCard, setShowUseAnotherCard] = useState(() => hasDirectPaymentFailureFlag());
  // Close X only after content has loaded AND 3s have passed (avoids accidental dismiss).
  const [showClose, setShowClose] = useState(false);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      setLoading(false);
      setCardInfo({ hasCard: false });
      return;
    }
    let cancelled = false;
    setShowClose(false);
    const promises: Promise<void>[] = [
      apiService.getPaymentInfo(userId)
        .then((info) => { if (!cancelled) setCardInfo(info); })
        .catch(() => { if (!cancelled) setCardInfo({ hasCard: false }); }),
    ];
    if (action.type === 'upgrade' && action.priceId) {
      promises.push(
        apiService.previewUpgrade(userId, action.priceId)
          .then((preview) => { if (!cancelled) { setProratedAmount(preview.amountDue); setIsTrialUpgrade(preview.isTrialUpgrade); } })
          .catch(() => {})
      );
    }
    Promise.all(promises).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.id, action.type, action.priceId]);

  useEffect(() => {
    if (loading) {
      setShowClose(false);
      return;
    }
    const timer = window.setTimeout(() => setShowClose(true), 3000);
    return () => window.clearTimeout(timer);
  }, [loading]);

  const handleConfirm = async () => {
    if (!user?.id || processing) return;
    setProcessing(true);
    setError(null);
    try {
      const userId = user.id;

      const transactionId = generateTransactionId();

      if (action.type === 'upgrade' && action.priceId) {
        await apiService.directUpgradePlan(userId, action.priceId);
        const value = proratedAmount ?? parsePriceString(action.amount);
        trackPurchase({
          planType: action.priceId,
          value,
          isSubscription: true,
          transactionId,
          metaEvent: 'PlanUpgrade', // existing customer: not a Meta Purchase
        });
      } else if (action.type === 'credits' && action.creditAmount) {
        await apiService.directBuyCredits(userId, action.creditAmount);
        trackCreditsPurchase(
          action.creditAmount,
          parsePriceString(action.amount),
          transactionId,
        );
      }

      clearDirectPaymentFailureFlag();
      setShowUseAnotherCard(false);
      setSuccess(true);
      await refreshUser();
      setTimeout(() => { onSuccess(); window.location.reload(); }, 1500);
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('errors.somethingWentWrong');
      setError(msg);
      setDirectPaymentFailureFlag();
      setShowUseAnotherCard(true);
    } finally {
      setProcessing(false);
    }
  };

  /** Redirect to Stripe Checkout so the user can pay / save a different card. */
  const handleUseAnotherCard = async () => {
    if (!user?.id || fallbackLoading) return;
    setFallbackLoading(true);
    setError(null);
    try {
      const userId = user.id;
      const email = user.email || undefined;

      if (action.type === 'credits' && action.creditAmount) {
        const { checkoutUrl } = await apiService.createCreditPurchaseSession(
          userId,
          action.creditAmount,
          email,
          `${window.location.origin}/subscription?success=true&credits=${action.creditAmount}`,
          `${window.location.origin}/subscription?canceled=true`,
        );
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }
        setError(t('errors.somethingWentWrong'));
      } else if (action.type === 'upgrade' && action.priceId) {
        // Setup-mode checkout: collect a new card, then return here to adopt it
        // and retry the yearly switch on the saved method.
        const { checkoutUrl } = await apiService.createCheckoutSession(
          userId,
          'yearly',
          email,
          undefined,
          `${window.location.origin}/subscription?update_card=true&price_id=${encodeURIComponent(action.priceId)}&session_id={CHECKOUT_SESSION_ID}`,
          `${window.location.origin}/subscription?canceled=true`,
          true,
          action.priceId,
          undefined,
          undefined,
          'update_card',
        );
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }
        setError(t('errors.somethingWentWrong'));
      } else if (action.onFallback) {
        action.onFallback();
      } else {
        setError(t('errors.somethingWentWrong'));
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || t('errors.somethingWentWrong'));
    } finally {
      setFallbackLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center animate-fade-in">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="relative z-10 w-full max-w-md mx-4 rounded-2xl bg-surface border border-glass-border shadow-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <h2 className="text-xl font-bold text-text-primary mb-2">{t('common.success')}!</h2>
          <p className="text-sm text-text-secondary">{action.type === 'credits' ? t('subscription.creditsPurchaseSuccess', { amount: action.creditAmount }) : action.label}</p>
        </div>
      </div>
    );
  }

  const brandLabel = cardInfo?.brand ? (CARD_BRAND_LABELS[cardInfo.brand] || cardInfo.brand) : '';

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center animate-fade-in">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={showClose ? onClose : undefined}
      />
      <div className="relative z-10 w-full max-w-md mx-4 rounded-2xl bg-surface border border-glass-border shadow-2xl">
        {showClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-surface-light transition-colors z-20 animate-fade-in"
            aria-label={t('common.close', { defaultValue: 'Close' })}
          >
            <X className="w-5 h-5 text-text-tertiary" />
          </button>
        )}

        <div className="p-6 md:p-8">
          {loading ? (
            <div className="animate-pulse">
              <div className="text-center mb-6">
                <div className="h-6 w-40 bg-surface-light rounded-lg mx-auto mb-2" />
                <div className="h-4 w-56 bg-surface-light rounded-lg mx-auto" />
              </div>
              <div className="p-4 rounded-xl bg-surface-light border border-glass-border mb-6 text-center">
                <div className="h-4 w-28 bg-glass-border rounded mx-auto mb-2" />
                <div className="h-9 w-32 bg-glass-border rounded-lg mx-auto" />
              </div>
              <div className="p-4 rounded-xl bg-surface-light border border-glass-border mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-8 bg-glass-border rounded" />
                  <div className="flex-1">
                    <div className="h-4 w-36 bg-glass-border rounded mb-1.5" />
                    <div className="h-3 w-16 bg-glass-border rounded" />
                  </div>
                </div>
              </div>
              <div className="h-12 w-full bg-glass-border rounded-xl" />
            </div>
          ) : (
          <>
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-text-primary mb-1">{action.label}</h2>
            <p className="text-sm text-text-secondary">{action.description}</p>
          </div>

          <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 mb-6 text-center">
            {action.type === 'upgrade' && proratedAmount !== null ? (
              <>
                <p className="text-sm text-text-secondary mb-1">{t('subscription.paidUpsell.chargedNow')}</p>
                <p className="text-3xl font-bold text-primary">${proratedAmount.toFixed(2)}</p>
                <p className="text-xs text-text-tertiary mt-1">{action.amount}</p>
              </>
            ) : (
              <>
                <p className="text-sm text-text-secondary mb-1">{t('subscription.totalPrice')}</p>
                <p className="text-3xl font-bold text-primary">{action.amount}</p>
              </>
            )}
          </div>

          {isTrialUpgrade && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-500">{t('subscription.paidUpsell.trialEndsNotice')}</p>
            </div>
          )}

          {cardInfo?.hasCard ? (
            <>
              <div className="mb-6">
                <div className="p-4 rounded-xl bg-surface-light border border-glass-border">
                  <div className="flex items-center gap-3">
                    <CardBrandLogo brand={cardInfo.brand} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary">{brandLabel} ****{cardInfo.last4}</p>
                      <p className="text-xs text-text-tertiary">{cardInfo.expMonth}/{cardInfo.expYear}</p>
                    </div>
                    <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>
                {showUseAnotherCard && (
                  <button
                    type="button"
                    onClick={() => void handleUseAnotherCard()}
                    disabled={processing || fallbackLoading}
                    className="w-full mt-2 py-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
                  >
                    {fallbackLoading ? t('common.loading') : t('subscription.useAnotherCard')}
                  </button>
                )}
              </div>

              {error && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-500">{error}</p>
                </div>
              )}

              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-base"
                onClick={handleConfirm}
                isLoading={processing}
                disabled={fallbackLoading}
              >
                {t('common.confirm')} {action.type === 'upgrade' && proratedAmount !== null ? `$${proratedAmount.toFixed(2)}` : action.amount}
              </Button>

              <div className="flex items-center justify-center gap-1.5 mt-3">
                <Shield className="w-3.5 h-3.5 text-text-tertiary" />
                <p className="text-xs text-text-tertiary">{t('subscription.securePaymentDesc')}</p>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-text-secondary mb-4">{t('subscription.noCardOnFile')}</p>
              <Button
                size="lg"
                className="w-full bg-primary hover:bg-primary/90"
                onClick={() => void handleUseAnotherCard()}
                isLoading={fallbackLoading}
              >
                {t('subscription.useAnotherCard')}
              </Button>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}

interface UseDirectPaymentReturn {
  openPayment: (action: PaymentAction) => void;
  paymentModal: React.ReactNode | null;
}

export function useDirectPayment(): UseDirectPaymentReturn {
  const [action, setAction] = useState<PaymentAction | null>(null);

  const openPayment = useCallback((a: PaymentAction) => setAction(a), []);
  const close = useCallback(() => setAction(null), []);

  const paymentModal = action ? (
    <ConfirmPaymentModal action={action} onClose={close} onSuccess={close} />
  ) : null;

  return { openPayment, paymentModal };
}

export function useEmbeddedCheckout() {
  const { openPayment, paymentModal } = useDirectPayment();
  return {
    openCheckout: openPayment,
    checkoutModal: paymentModal,
    isLoading: false,
  };
}
